import { useCallback, useEffect, useState } from "react";

// Not in lib.dom yet: Chromium-only, still an unratified proposal.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "install-prompt-dismissed";

// Chrome only fires beforeinstallprompt after a tap and ~30s on the page, so
// by then the user has settled in. iOS has no such gate, hence the delay.
const IOS_DELAY_MS = 20_000;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  // iOS predates display-mode and sets this instead.
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIOS = () => {
  const { userAgent, maxTouchPoints } = navigator;
  return (
    /iPhone|iPad|iPod/.test(userAgent) ||
    // iPadOS reports itself as a Mac; touch points are what give it away.
    (userAgent.includes("Macintosh") && maxTouchPoints > 1)
  );
};

type Variant =
  | "prompt" // Chromium: we hand the user off to the browser's own dialog.
  | "instructions" // iOS: no API exists, so we can only explain the taps.
  | null;

const useInstallPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [iosReady, setIosReady] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "true",
  );

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Suppress the browser's own promotion so we control the moment.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferred(null);

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!isIOS()) return;
    const timer = setTimeout(() => setIosReady(true), IOS_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  }, []);

  const install = useCallback(() => {
    if (!deferred) return;
    // The event is single-use, so drop it as soon as it has been spent.
    setDeferred(null);
    deferred
      .prompt()
      .then(() => deferred.userChoice)
      .then(({ outcome }) => {
        if (outcome === "dismissed") dismiss();
      })
      .catch(() => {
        // Already consumed, or the browser refused. Nothing to recover.
      });
  }, [deferred, dismiss]);

  let variant: Variant = null;
  if (!dismissed && !isStandalone()) {
    if (deferred) variant = "prompt";
    else if (iosReady) variant = "instructions";
  }

  return { variant, install, dismiss };
};

export default useInstallPrompt;
